import { useMemo, useState } from 'react';
import { IconAnchor, IconArrowLeft, IconBan, IconBuilding, IconBuildingWarehouse, IconChevronLeft, IconClock, IconCopy, IconDots, IconExternalLink, IconFocusCentered, IconMapPin, IconMinus, IconPencil, IconPlus, IconRefresh, IconRoute, IconSearch, IconShip, IconTarget, IconTruck, IconUser, IconTrafficCone, type Icon } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useNavigate } from '@tanstack/react-router';
import { Page, PageHeader } from '@hana/shell';
import { Button, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { AREAS, GEO, KIND_COLOR, KIND_ORDER, PENDING_ALL, TOTALS, WH_PROVINCES, WH_SUBS, WH_VEHICLES, WORLD, camera, carriersOf, filterPlaces, filterWarehouses, whRestrict, whRestrictText, whStatus, type WhSub, fmtRadius, fmtXY, hasXY, isPending, outOfFrame, project, provinceOf, ringDiameter, routeRows, statusOf, type SubKey } from './places';
import type { Place, PlaceKind } from './types';
import { MAP_ROUTES, TOLLS, TOLL_COLOR, CARGO_COLOR, ROUTE_TOTAL, TOLL_TOTAL, cai, routePlace, routesOfToll, tollPlace, whOf } from './mapRoutes';
import { RtDetail, RtListPanel, TollDetail, TollListPanel } from './PlacesRtToll';
import { useT } from '@hana/i18n';
import './i18n';
import s from './places.module.css';

// Mint-TMS-Places (desktop 1536) · loại "ICD · Depot". Bản đồ nền vẽ theo toạ độ (chưa nối VietMap), camera bay tới điểm được chọn.
// Loại Kho · Tuyến · Trạm thu phí đã có (Tuyến / Trạm từ dữ liệu mẫu mapRoutes). Chưa làm: form Sửa / Thêm điểm, ô tìm địa điểm VietMap, khổ 1920 + iPad.
// Nút ghi chỉ báo toast (chưa ghi DB).

const KIND_ICON: Record<PlaceKind, Icon> = { Cảng: IconAnchor, ICD: IconBuilding, Depot: IconBuildingWarehouse, Kho: IconBuildingWarehouse, Tuyến: IconRoute, Trạm: IconTrafficCone };
const KIND_TAG: Record<'DC' | 'RF' | '', string> = { DC: 'var(--d-blue)', RF: 'var(--d-violet)', '': 'var(--d-gray)' };
const STATUS_DOT = { ok: 'var(--ok-dot)', warn: 'var(--d-amber)', err: 'var(--err)' } as const;
const STATUS_CHIP = { ok: s.stOk, warn: s.stWarn, err: s.stErr } as const;
type Mode = 'dep' | 'wh' | 'rt' | 'toll';
const TOTAL_OF: Record<Mode, number> = { dep: TOTALS.dep, wh: TOTALS.wh, rt: ROUTE_TOTAL, toll: TOLL_TOTAL };
const RT_LEGEND: ReadonlyArray<readonly [string, string]> = [['Chiều đi', 'var(--d-blue)'], ['Chiều về', 'var(--lb)'], ['Trạm thu phí', 'var(--d-amber)'], ['Kho', 'var(--ok-dot)']];
const TOLL_LEGEND: ReadonlyArray<readonly [string, string]> = [['Thu kín', TOLL_COLOR.KIN], ['Thu mở', TOLL_COLOR.MO], ['Tin cậy thấp', 'var(--err)']];
const LEGEND: ReadonlyArray<readonly [string, string]> = [['Cảng', 'var(--d-blue)'], ['ICD', 'var(--d-green)'], ['Depot', 'var(--d-violet)'], ['Chờ duyệt', 'var(--d-amber)']];
const WH_LEGEND: ReadonlyArray<readonly [string, string]> = [['Đã xác thực', 'var(--ok-dot)'], ['Chưa xác thực', 'var(--d-amber)'], ['Hạn chế xe', 'var(--warn)']];
const MAP_W = 1264; // 1536 − sidebar 248 − 2×12
const MAP_H = 904; // board cao 980 − đầu trang 64 − 12

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function Sec({ icon: Ico, title, hint, children }: { icon: Icon; title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section aria-label={title} className={s.sec}>
      <h3 className={s.secH}>
        <Ico size={14} stroke={2} aria-hidden="true" />
        {title}
        {hint && <span role="img" aria-label={hint} title={hint} className={s.info}>i</span>}
      </h3>
      {children}
    </section>
  );
}

function Detail({ p, onBack }: { p: Place; onBack: () => void }) {
  const toast = useToast();
  const t = useT();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const st = statusOf(p);
  const crs = carriersOf(p.carriers);
  const Ico = KIND_ICON[p.kind];
  const rows = routeRows(p);
  const hrs = p.hours ?? '';
  const stats: ReadonlyArray<[Icon, string, string | number, boolean]> = [
    [IconShip, 'Hãng', crs.length || '—', true],
    [IconRoute, 'Tuyến', p.routes || '—', true],
    [IconTarget, 'Geofence', fmtRadius(p.radius), !!p.radius],
    [IconClock, 'Giờ', hrs || '—', !!hrs],
  ];
  return (
    <section aria-label={p.name} className={`${s.panel} ${s.detail}`}>
      <header className={s.dHead}>
        <button type="button" className={s.sq30} aria-label="Về danh sách" title="Về danh sách" onClick={onBack}>
          <IconChevronLeft size={18} stroke={2} aria-hidden="true" />
        </button>
        <span className={s.dIc} title={`${p.kind} · ${p.area ?? ''}`} style={{ background: `color-mix(in srgb, ${KIND_COLOR[p.kind]} 12%, transparent)`, color: KIND_COLOR[p.kind] }}>
          <Ico size={20} stroke={1.9} aria-hidden="true" />
        </span>
        <div className={s.dTx}>
          <h2 className={s.dTitle} title={`${p.kind} · ${p.area ?? ''}`}>{p.name}</h2>
          <div className={s.dMeta}>
            <span className={s.code}>{p.code}</span>
            <span className={`${s.chipSt} ${STATUS_CHIP[st.key]}`}><i style={{ background: STATUS_DOT[st.key] }} />{st.label}</span>
          </div>
        </div>
      </header>
      <div className={s.dBody}>
        <div className={s.acts}>
          <button type="button" className={s.act} onClick={noWrite}><IconPencil size={16} stroke={1.8} aria-hidden="true" />{t('tms.edit')}</button>
          {hasXY(p) && <button type="button" className={`${s.act} ${s.actGhost}`} onClick={() => toast({ msg: `Đã sao ${p.lat}, ${p.lng}` })}><IconCopy size={16} stroke={1.8} aria-hidden="true" />{t('tms.copyCoords')}</button>}
        </div>
        <div className={s.stats}>
          {stats.map(([I, k, v, on]) => (
            <div key={k} className={s.stat}>
              <div className={s.statK}><I size={13} stroke={2} aria-hidden="true" />{k}</div>
              <b className={on ? '' : s.dim}>{v}</b>
            </div>
          ))}
        </div>
        <Sec icon={IconShip} title="Hãng tàu trả / lấy rỗng" hint={crs.length ? '● DC  ● RF  ● cả hai' : undefined}>
          {crs.length ? (
            <div className={s.tags}>
              {crs.map((c) => <span key={c.code} className={s.tag}><i style={{ background: KIND_TAG[c.type] }} />{c.code + (c.type ? ` · ${c.type}` : '')}</span>)}
            </div>
          ) : <div className={s.note}>{t('tms.noCarrier')}</div>}
        </Sec>
        <Sec icon={IconMapPin} title="Vị trí">
          <dl className={s.kv}>
            <dt>{t('tms.coords')}</dt><dd className={hasXY(p) ? s.coord : s.coordErr}>{fmtXY(p)}</dd>
            <dt>{t('tms.area')}</dt><dd>{p.area}</dd>
            <dt>{t('tms.province')}</dt><dd>{provinceOf(p)}</dd>
          </dl>
        </Sec>
        <Sec icon={IconClock} title="Giờ & cổng" hint="Tài khoản cổng chỉ hiện trong Sửa · ghế Trưởng Vận tải">
          <dl className={s.kv}>
            <dt>{t('tms.hours')}</dt><dd className={hrs ? '' : s.dim}>{hrs || 'Chưa nhập'}</dd>
            <dt>{t('tms.ecus')}</dt><dd className={p.ecus ? '' : s.dim}>{p.ecus || '—'}</dd>
            <dt>ePort</dt><dd className={p.eport ? s.link : s.dim}>{p.eport ? 'Có liên kết' : '—'}</dd>
          </dl>
        </Sec>
        {rows.length > 0 && (
          <Sec icon={IconRoute} title="Tuyến xuất phát">
            <div className={s.list}>
              {rows.map((r, i) => (
                <div key={r.wh} className={s.lrow} title={`RRF${900 + i * 7 + p.code.length}`}>
                  <i className={s.dot8} style={{ background: 'var(--d-violet)' }} />
                  <div className={s.lk}>{r.wh}</div>
                  <b>{`${String(r.km).replace('.', ',')} km`}</b>
                  <button type="button" className={s.lBtn} onClick={noWrite}><IconExternalLink size={16} stroke={1.8} aria-hidden="true" />{t('tms.open')}</button>
                </div>
              ))}
            </div>
            <div className={s.note}>{`${p.routes} tuyến trong danh mục`}</div>
          </Sec>
        )}
      </div>
    </section>
  );
}

function WhDetail({ p, onBack }: { p: Place; onBack: () => void }) {
  const toast = useToast();
  const t = useT();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const st = whStatus(p);
  const r = whRestrict(p);
  const cust = (p.customers ?? '').split(' ').filter(Boolean);
  const stats: ReadonlyArray<[Icon, string, string | number, boolean]> = [
    [IconUser, 'Khách', cust.length || '—', cust.length > 0],
    [IconRoute, 'Tuyến', p.routes || '—', true],
    [IconTarget, 'Geofence', fmtRadius(p.radius), !!p.radius],
    [IconClock, 'Giờ', p.hours || '—', !!p.hours],
  ];
  return (
    <section aria-label={p.name} className={`${s.panel} ${s.detail}`}>
      <header className={s.dHead}>
        <button type="button" className={s.sq30} aria-label="Về danh sách" title="Về danh sách" onClick={onBack}>
          <IconChevronLeft size={18} stroke={2} aria-hidden="true" />
        </button>
        <span className={s.dIc} title={`Kho · ${p.area ?? ''}`} style={{ background: `color-mix(in srgb, ${KIND_COLOR.Kho} 12%, transparent)`, color: KIND_COLOR.Kho }}>
          <IconBuildingWarehouse size={20} stroke={1.9} aria-hidden="true" />
        </span>
        <div className={s.dTx}>
          <h2 className={s.dTitle} title={`Kho · ${p.area ?? ''}`}>{p.name}</h2>
          <div className={s.dMeta}>
            <span className={s.code}>{p.code}</span>
            <span className={`${s.chipSt} ${STATUS_CHIP[st.key]}`}><i style={{ background: STATUS_DOT[st.key] }} />{st.label}</span>
          </div>
        </div>
      </header>
      <div className={s.dBody}>
        <div className={s.acts}>
          <button type="button" className={s.act} onClick={noWrite}><IconPencil size={16} stroke={1.8} aria-hidden="true" />{t('tms.edit')}</button>
          {hasXY(p) && <button type="button" className={`${s.act} ${s.actGhost}`} onClick={() => toast({ msg: `Đã sao ${p.lat}, ${p.lng}` })}><IconCopy size={16} stroke={1.8} aria-hidden="true" />{t('tms.copyCoords')}</button>}
        </div>
        <div className={s.stats}>
          {stats.map(([I, k, v, on]) => (
            <div key={k} className={s.stat}>
              <div className={s.statK}><I size={13} stroke={2} aria-hidden="true" />{k}</div>
              <b className={on ? '' : s.dim}>{v}</b>
            </div>
          ))}
        </div>
        <Sec icon={IconBan} title="Hạn chế xe" hint="Điều độ được cảnh báo khi xếp xe sai loại hoặc giao trong giờ cấm">
          {r ? (
            <div className={s.tags}>
              {r.ban.map((b) => <span key={b} className={s.tag} title="Giờ cấm tải"><IconClock size={12} stroke={2} aria-hidden="true" />{b}</span>)}
              {r.ton ? <span className={s.tag} title="Tải trọng tối đa">{`≤ ${r.ton} tấn`}</span> : null}
              {r.veh.length > 0 && WH_VEHICLES.map((v) => { const ok = r.veh.includes(v); return <span key={v} className={s.tag} title={ok ? 'Được vào kho' : 'Không được vào kho'} style={ok ? undefined : { opacity: 0.55 }}>{ok ? <IconTruck size={12} stroke={2} aria-hidden="true" /> : <IconBan size={12} stroke={2} aria-hidden="true" />}{v}</span>; })}
              {r.road ? <span className={s.tag} title="Đường / khu cấm xe lớn">{r.road}</span> : null}
            </div>
          ) : <div className={s.note}>{whRestrictText(p) || 'Không hạn chế'}</div>}
        </Sec>
        <Sec icon={IconUser} title="Khách tại kho">
          {cust.length ? <div className={s.tags}>{cust.map((c) => <span key={c} className={s.tag}>{c}</span>)}</div> : <div className={s.note}>{t('tms.noCustomer')}</div>}
        </Sec>
        <Sec icon={IconMapPin} title="Vị trí">
          <dl className={s.kv}>
            <dt>{t('tms.coords')}</dt><dd className={hasXY(p) ? s.coord : s.coordErr}>{fmtXY(p)}</dd>
            <dt>{t('tms.address')}</dt><dd className={p.address ? '' : s.dim}>{p.address || '—'}</dd>
            <dt>{t('tms.ward')}</dt><dd className={p.ward ? '' : s.dim}>{p.ward || '—'}</dd>
            <dt>{t('tms.province')}</dt><dd>{p.province}</dd>
          </dl>
        </Sec>
        {p.note && <Sec icon={IconClock} title="Ghi chú"><div className={s.note}>{p.note}</div></Sec>}
      </div>
    </section>
  );
}

function WhListPanel({ all, q, onPick, state, onRetry }: { all: readonly Place[]; q: string; onPick: (code: string) => void; state: ScreenState; onRetry: () => void }) {
  const toast = useToast();
  const t = useT();
  const [sub, setSub] = useState<WhSub>('all');
  const [pend, setPend] = useState(false);
  const list = useMemo(() => filterWarehouses(all, sub, q, pend), [all, sub, q, pend]);
  const nPend = all.filter((p) => !p.approved).length;
  const count = (k: WhSub) => filterWarehouses(all, k, '', false).length;
  return (
    <section aria-label="Kho" className={`${s.panel} ${s.lp}`}>
      <div className={s.lpHead}>
        <div className={s.lpTop}>
          <h2 className={s.lpH}>Kho</h2>
          <span className={s.lpCount} title={`${list.length} kho đang hiện / ${TOTALS.wh} trong danh mục`}>{`${list.length} / ${TOTALS.wh}`}</span>
          <button type="button" className={s.sq30} aria-label="Làm mới" title="Làm mới" onClick={() => toast({ msg: 'Đã tải lại Kho' })}><IconRefresh size={15} stroke={2} aria-hidden="true" /></button>
        </div>
        <div className={s.pills}>
          {WH_SUBS.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={sub === k} className={`${s.pill} ${sub === k ? s.pillOn : ''}`} onClick={() => setSub(k)}>{label}<span>{count(k)}</span></button>
          ))}
          {nPend > 0 && (
            <button type="button" aria-pressed={pend} className={`${s.pill} ${pend ? s.pillOn : ''}`} title={t('tms.unverified')} onClick={() => setPend(!pend)}><i style={{ background: 'var(--d-amber)' }} />{nPend}<span>{t('tms.unverified')}</span></button>
          )}
        </div>
      </div>
      <div role="listbox" aria-label="Kho" className={s.lpBody}>
        <StateView
          state={state}
          icon={<IconMapPin size={26} stroke={1.8} />}
          title={state === 'empty' ? 'Chưa có kho nào' : undefined}
          action={state === 'error' ? <Button variant="secondary" onClick={onRetry}>{t('common.retry')}</Button> : undefined}
          skeleton={<div aria-busy="true" className={s.skel}>{[1, 2, 3, 4, 5, 6, 7, 8].map((k) => <Skeleton key={k} />)}</div>}
        >
          {WH_PROVINCES.map(([pv, color]) => {
            const its = list.filter((p) => p.province === pv);
            if (!its.length) return null;
            return (
              <div key={pv}>
                <div className={s.grp}><i style={{ background: color }} />{pv}<span>{its.length}</span></div>
                {its.map((p) => {
                  const st = whStatus(p);
                  const nCu = (p.customers ?? '').split(' ').filter(Boolean).length;
                  const rs = whRestrictText(p);
                  return (
                    <button key={p.code} type="button" role="option" aria-selected={false} className={s.item} title={[p.code, p.area, st.label].filter(Boolean).join(' · ')} onClick={() => onPick(p.code)}>
                      <span className={s.itIc} style={{ background: `color-mix(in srgb, ${KIND_COLOR.Kho} 12%, transparent)`, color: KIND_COLOR.Kho }}><IconBuildingWarehouse size={16} stroke={1.9} aria-hidden="true" /></span>
                      <b>{p.name}</b>
                      <span className={s.mets}>
                        {rs && <span title={`Hạn chế xe · ${rs}`} style={{ color: 'var(--warn)' }}><IconBan size={13} stroke={2} aria-hidden="true" /></span>}
                        {nCu > 0 && <span title={`${nCu} khách`}><IconUser size={13} stroke={2} aria-hidden="true" />{nCu}</span>}
                        {!!p.routes && <span title={`${p.routes} tuyến`}><IconRoute size={13} stroke={2} aria-hidden="true" />{p.routes}</span>}
                      </span>
                      <span role="img" aria-label={st.label} title={st.label} className={s.dot8} style={{ background: STATUS_DOT[st.key] }} />
                    </button>
                  );
                })}
              </div>
            );
          })}
          {list.length === 0 && <div className={s.none}>{t('tms.noPlaceMatch')}</div>}
        </StateView>
      </div>
    </section>
  );
}

function ListPanel({ all, q, onPick, state, onRetry }: { all: readonly Place[]; q: string; onPick: (code: string) => void; state: ScreenState; onRetry: () => void }) {
  const toast = useToast();
  const t = useT();
  const [sub, setSub] = useState<SubKey>('all');
  const [pend, setPend] = useState(false);
  const list = useMemo(() => filterPlaces(all, sub, q, pend), [all, sub, q, pend]);
  const nPend = all.filter(isPending).length;
  const subs: ReadonlyArray<{ key: SubKey; label: string; n: number }> = [{ key: 'all', label: 'Tất cả', n: all.length }, ...KIND_ORDER.map((k) => ({ key: k, label: k, n: all.filter((p) => p.kind === k).length }))];
  return (
    <section aria-label="ICD · Depot" className={`${s.panel} ${s.lp}`}>
      <div className={s.lpHead}>
        <div className={s.lpTop}>
          <h2 className={s.lpH}>ICD · Depot</h2>
          <span className={s.lpCount} title={`${list.length} điểm đang hiện / ${TOTALS.dep} trong danh mục`}>{`${list.length} / ${TOTALS.dep}`}</span>
          <button type="button" className={s.sq30} aria-label="Làm mới" title="Làm mới" onClick={() => toast({ msg: 'Đã tải lại ICD · Depot' })}><IconRefresh size={15} stroke={2} aria-hidden="true" /></button>
        </div>
        <div className={s.pills}>
          {subs.map((f) => (
            <button key={f.key} type="button" aria-pressed={sub === f.key} className={`${s.pill} ${sub === f.key ? s.pillOn : ''}`} onClick={() => setSub(f.key)}>{f.label}<span>{f.n}</span></button>
          ))}
          {nPend > 0 && (
            <button type="button" aria-pressed={pend} className={`${s.pill} ${pend ? s.pillOn : ''}`} title="chờ duyệt" onClick={() => setPend(!pend)}><i style={{ background: 'var(--d-amber)' }} />{nPend}<span>{t('tms.awaitApproval')}</span></button>
          )}
        </div>
      </div>
      <div role="listbox" aria-label="ICD · Depot" className={s.lpBody}>
        <StateView
          state={state}
          icon={<IconMapPin size={26} stroke={1.8} />}
          title={state === 'empty' ? 'Chưa có điểm nào' : undefined}
          action={state === 'error' ? <Button variant="secondary" onClick={onRetry}>{t('common.retry')}</Button> : undefined}
          skeleton={<div aria-busy="true" className={s.skel}>{[1, 2, 3, 4, 5, 6, 7, 8].map((k) => <Skeleton key={k} />)}</div>}
        >
          {KIND_ORDER.map((k) => {
            const its = list.filter((p) => p.kind === k);
            if (!its.length) return null;
            return (
              <div key={k}>
                <div className={s.grp}><i style={{ background: KIND_COLOR[k] }} />{k}<span>{its.length}</span></div>
                {its.map((p) => {
                  const I = KIND_ICON[p.kind];
                  const st = statusOf(p);
                  const nCr = carriersOf(p.carriers).length;
                  return (
                    <button key={p.code} type="button" role="option" aria-selected={false} className={s.item} title={[p.code, p.area, st.label].filter(Boolean).join(' · ')} onClick={() => onPick(p.code)}>
                      <span className={s.itIc} style={{ background: `color-mix(in srgb, ${KIND_COLOR[p.kind]} 12%, transparent)`, color: KIND_COLOR[p.kind] }}><I size={16} stroke={1.9} aria-hidden="true" /></span>
                      <b>{p.name}</b>
                      <span className={s.mets}>
                        {p.hours === '24/7' && <span title="Hoạt động 24/7"><IconClock size={13} stroke={2} aria-hidden="true" />24h</span>}
                        {nCr > 0 && <span title={`${nCr} hãng trả / lấy rỗng`}><IconShip size={13} stroke={2} aria-hidden="true" />{nCr}</span>}
                        {!!p.routes && <span title={`${p.routes} tuyến`}><IconRoute size={13} stroke={2} aria-hidden="true" />{p.routes}</span>}
                      </span>
                      <span role="img" aria-label={st.label} title={st.label} className={s.dot8} style={{ background: STATUS_DOT[st.key] }} />
                    </button>
                  );
                })}
              </div>
            );
          })}
          {list.length === 0 && <div className={s.none}>{t('tms.noPlaceMatch')}</div>}
        </StateView>
      </div>
    </section>
  );
}

export function PlacesPage() {
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const { data, error, isLoading, isError, refetch } = useList('tmsPlace', { limit: 500 });
  const forced = readState();
  const everything = data?.items ?? [];
  const [mode, setMode] = useState<Mode>('dep');
  const isWh = mode === 'wh';
  const isRt = mode === 'rt';
  const isToll = mode === 'toll';
  const [approved, setApproved] = useState<ReadonlySet<string>>(new Set());
  const all = useMemo(() => (isRt ? MAP_ROUTES.map(routePlace) : isToll ? TOLLS.map(tollPlace) : everything.filter((p) => (p.kind === 'Kho') === isWh)), [everything, isWh, isRt, isToll]);
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const [selCode, setSel] = useState<string | null | undefined>(undefined);
  const [q, setQ] = useState('');
  const [zoom, setZoom] = useState(0);

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (everything.length === 0) state = 'empty';
  const ready = state === 'ready' || state === 'noperm';

  const DEF: Record<Mode, string | null> = { dep: 'TCMT', wh: null, rt: 'RRF0002', toll: 'TS0118' }; // board mở sẵn TCMT · RRF0002 · TS0118
  const code = selCode === undefined ? DEF[mode] : selCode;
  const sel = ready ? (all.find((p) => p.code === code) ?? null) : null;
  const pick = (c: string | null | undefined) => { setSel(c); setZoom(0); };
  const cam = camera(all, sel, MAP_W, MAP_H, sel ? 420 : 360, zoom);
  const shown = ready ? all.filter(hasXY) : [];
  const out = isRt || isToll ? { n: 0, side: 'Bắc' } : outOfFrame(all, sel);
  const legendL = 12 + (sel ? 420 : 360) + 12;

  return (
    <Page kind="tool" pad={12}>
      <PageHeader findPad
        title="Bản đồ điểm & tuyến"
        findHint={false}
        lead={
          <button type="button" className={s.back} aria-label="Về Vận tải" title="Về Vận tải" onClick={() => void navigate({ to: '/ezy/tms' })}>
            <IconArrowLeft size={18} aria-hidden="true" />
          </button>
        }
        tabs={ready ? <button type="button" className={s.hChip} title="Chờ duyệt · chưa xác thực · NeedVerify · tin cậy thấp" onClick={() => toast({ msg: 'Đã lọc điểm chờ xử lý' })}><i />{`${PENDING_ALL} chờ xử lý`}</button> : undefined}
        actions={
          <>
            {state !== 'noperm' && <Button variant="primary" icon={<IconPlus size={16} />} onClick={noWrite}>{t(isRt ? 'tms.addRoute' : isToll ? 'tms.addToll' : 'tms.addPlace')}</Button>}
            <button type="button" className={s.more} aria-label="Thêm thao tác" title="Thêm thao tác" onClick={noWrite}><IconDots size={18} aria-hidden="true" /></button>
          </>
        }
      />
      <div className={s.mapWrap}>
        <div role="img" aria-label={`${isRt ? 'Tuyến' : isToll ? 'Trạm thu phí' : isWh ? 'Kho' : 'ICD · Depot'} trên bản đồ: ${shown.length} điểm${sel ? ` · đang chọn ${sel.name}` : ''}`} className={s.map} onClick={() => undefined}>
          <div className={s.world} style={{ width: WORLD.w, height: WORLD.h, transform: `translate(${cam.tx.toFixed(1)}px, ${cam.ty.toFixed(1)}px) scale(${cam.z.toFixed(4)})` }}>
            <svg aria-hidden="true" width={WORLD.w} height={WORLD.h} viewBox={`0 0 ${WORLD.w} ${WORLD.h}`} className={s.worldSvg}>
              <path d={GEO.sea} className={s.water} />
              {GEO.rivers.map((l) => <path key={l.d} d={l.d} className={s.riv} strokeWidth={l.w} vectorEffect="non-scaling-stroke" />)}
              {GEO.roads.map((l) => <path key={l.d} d={l.d} className={s.rd} strokeWidth={l.w} vectorEffect="non-scaling-stroke" />)}
            </svg>
          </div>
          {AREAS.map(([k, la, lo]) => {
            const p = project(cam, la, lo);
            return <span key={k} aria-hidden="true" className={s.area} style={{ left: p.x, top: p.y, opacity: cam.z < 3.2 ? 1 : 0 }}>{k}</span>;
          })}
          {sel && hasXY(sel) && !!sel.radius && (() => {
            const p = project(cam, sel.lat, sel.lng);
            const d = ringDiameter(cam, sel.radius);
            return <span aria-hidden="true" title="Geofence" className={s.ring} style={{ left: p.x, top: p.y, width: d, height: d }} />;
          })()}
          {ready && (isRt || isToll) && (() => {
            const cl = cai();
            const A = project(cam, cl.lat as number, cl.lng as number);
            const curve = (b: { x: number; y: number }, bend: number) => { const mx = (A.x + b.x) / 2, my = (A.y + b.y) / 2, dx = b.x - A.x, dy = b.y - A.y; return `M${A.x.toFixed(1)} ${A.y.toFixed(1)} Q${(mx - dy * bend).toFixed(1)} ${(my + dx * bend).toFixed(1)} ${b.x.toFixed(1)} ${b.y.toFixed(1)}`; };
            const back = (b: { x: number; y: number }, bend: number) => { const mx = (A.x + b.x) / 2, my = (A.y + b.y) / 2, dx = A.x - b.x, dy = A.y - b.y; return `M${b.x.toFixed(1)} ${b.y.toFixed(1)} Q${(mx - dy * bend).toFixed(1)} ${(my + dx * bend).toFixed(1)} ${A.x.toFixed(1)} ${A.y.toFixed(1)}`; };
            const selRt = isRt && sel ? MAP_ROUTES.find((r) => r.id === sel.code) : undefined;
            const selToll = isToll && sel ? TOLLS.find((x) => x.id === sel.code) : undefined;
            return (
              <>
                <svg aria-hidden="true" className={s.lines}>
                  {isRt && shown.filter((p) => p.code !== sel?.code).map((p) => <path key={p.code} d={curve(project(cam, p.lat, p.lng), 0.18)} fill="none" stroke={CARGO_COLOR[MAP_ROUTES.find((r) => r.id === p.code)?.cargo ?? 'DC']} strokeWidth="1.5" opacity="0.35" />)}
                  {selRt && sel && hasXY(sel) && (() => { const b = project(cam, sel.lat, sel.lng); return <><path d={curve(b, 0.22)} fill="none" stroke="var(--d-blue)" strokeWidth="3.5" /><path d={back(b, 0.22)} fill="none" stroke="var(--lb)" strokeWidth="2" strokeDasharray="7 6" /></>; })()}
                  {selToll && routesOfToll(selToll).map((r) => { const w = whOf(r.wh); return w && hasXY(w) ? <path key={r.id} d={curve(project(cam, w.lat, w.lng), 0.2)} fill="none" stroke="var(--d-blue)" strokeWidth="2" opacity="0.6" /> : null; })}
                </svg>
                <button type="button" aria-label={cl.name} title={cl.name} className={s.pinHit} style={{ left: A.x, top: A.y }} onClick={(e) => { e.stopPropagation(); toast({ msg: 'Mở CAT LAI TMNL ở ICD · Depot' }); }}>
                  <span aria-hidden="true" className={`${s.pin} ${s.pinSq}`} style={{ background: 'var(--tx2)' }} />
                </button>
                {selRt && selRt.tolls.map(([k]) => { const tt = TOLLS.find((y) => y.name === k); if (!tt) return null; const b = project(cam, tt.lat, tt.lng); return <button key={k} type="button" aria-label={k} title={k} className={s.pinHit} style={{ left: b.x, top: b.y, zIndex: 2 }} onClick={(e) => { e.stopPropagation(); setMode('toll'); pick(tt.id); }}><span aria-hidden="true" className={`${s.pin} ${s.pinSq}`} style={{ background: TOLL_COLOR[tt.sys] }} /></button>; })}
              </>
            );
          })()}
          {shown.map((p) => {
            const on = sel?.code === p.code;
            const pos = project(cam, p.lat as number, p.lng as number);
            return (
              <button key={p.code} type="button" aria-label={p.name} title={p.name} className={s.pinHit} style={{ left: pos.x, top: pos.y, zIndex: on ? 3 : 1 }} onClick={(e) => { e.stopPropagation(); pick(on ? null : p.code); }}>
                <span aria-hidden="true" className={`${s.pin} ${on ? s.pinOn : ''} ${isToll ? s.pinSq : ''}`} style={{ background: isRt ? (on ? 'var(--ok-dot)' : 'var(--ctl-on)') : isToll ? TOLL_COLOR[TOLLS.find((x) => x.id === p.code)?.sys ?? 'MO'] : isPending(p) ? 'var(--d-amber)' : (isWh ? 'var(--ok-dot)' : KIND_COLOR[p.kind]) }} />
              </button>
            );
          })}
          {sel && hasXY(sel) && (() => {
            const p = project(cam, sel.lat, sel.lng);
            return <span className={s.pinLb} style={{ left: p.x, top: p.y }}>{sel.name}</span>;
          })()}
        </div>

        <div className={s.floatTop}>
          <label className={s.qBox} style={{ width: sel ? 420 : 360 }}>
            <IconSearch size={15} stroke={2.2} aria-hidden="true" />
            <input aria-label={isRt ? 'Tìm mã tuyến, kho, khách' : isToll ? 'Tìm trạm, hành lang' : 'Tìm điểm, kho, tuyến, địa điểm VietMap'} placeholder={isRt ? 'Tìm mã tuyến, kho, khách' : isToll ? 'Tìm trạm, hành lang' : 'Tìm điểm, kho, tuyến, địa điểm VietMap'} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div role="group" aria-label="Loại danh mục" className={s.kinds}>
            <button type="button" aria-pressed={mode === 'dep'} className={`${s.kindBtn} ${mode === 'dep' ? s.kindOn : ''}`} onClick={() => { setMode('dep'); pick(undefined); }}><IconBuildingWarehouse size={15} stroke={1.9} aria-hidden="true" />ICD · Depot<span>{TOTALS.dep}</span></button>
            <button type="button" aria-pressed={isWh} className={`${s.kindBtn} ${isWh ? s.kindOn : ''}`} onClick={() => { setMode('wh'); pick(undefined); }}><IconBuildingWarehouse size={15} stroke={1.9} aria-hidden="true" />Kho<span>{TOTALS.wh}</span></button>
            <button type="button" aria-pressed={isRt} className={`${s.kindBtn} ${isRt ? s.kindOn : ''}`} onClick={() => { setMode('rt'); pick(undefined); }}><IconRoute size={15} stroke={1.9} aria-hidden="true" />{t('tms.kindRoute')}<span>{TOTAL_OF.rt}</span></button>
            <button type="button" aria-pressed={isToll} className={`${s.kindBtn} ${isToll ? s.kindOn : ''}`} onClick={() => { setMode('toll'); pick(undefined); }}><IconTrafficCone size={15} stroke={1.9} aria-hidden="true" />{t('tms.kindToll')}<span>{TOTAL_OF.toll}</span></button>
          </div>
          <span className={s.grow} />
          {ready && out.n > 0 && <button type="button" className={s.mapNote} onClick={() => toast({ msg: `${out.n} điểm phía ${out.side}` })}>{`+${out.n} điểm phía ${out.side} ›`}</button>}
        </div>

        {isRt || isToll ? (
          isRt ? (
            sel && MAP_ROUTES.some((r) => r.id === sel.code)
              ? <RtDetail r={MAP_ROUTES.find((r) => r.id === sel.code) as (typeof MAP_ROUTES)[number]} approved={approved.has(sel.code)} onApprove={() => { setApproved(new Set([...approved, sel.code])); toast({ msg: `Đã duyệt ${sel.code}` }); }} onBack={() => pick(null)} onOpenToll={(id) => { setMode('toll'); pick(id); }} onPrice={() => void navigate({ to: '/ezy/tms/routes/price' })} />
              : <RtListPanel q={q} state={state} onRetry={() => void refetch()} approved={approved} onPick={pick} />
          ) : (
            sel && TOLLS.some((x) => x.id === sel.code)
              ? <TollDetail x={TOLLS.find((x) => x.id === sel.code) as (typeof TOLLS)[number]} onBack={() => pick(null)} onOpenRoute={(id) => { setMode('rt'); pick(id); }} />
              : <TollListPanel q={q} state={state} onRetry={() => void refetch()} onPick={pick} />
          )
        ) : sel ? (isWh ? <WhDetail p={sel} onBack={() => pick(null)} /> : <Detail p={sel} onBack={() => pick(null)} />) : isWh ? <WhListPanel all={all} q={q} onPick={pick} state={state} onRetry={() => void refetch()} /> : <ListPanel all={all} q={q} onPick={pick} state={state} onRetry={() => void refetch()} />}

        {state === 'noperm' && <div role="status" className={s.perm} style={{ left: legendL }}>{t('tms.viewOnlyShort')}</div>}
        <div className={s.legend} style={{ left: legendL }}>
          {(isRt ? RT_LEGEND : isToll ? TOLL_LEGEND : isWh ? WH_LEGEND : LEGEND).map(([k, c]) => <span key={k}><i style={{ background: c }} />{k}</span>)}
          <em>VietMap</em>
        </div>
        <div role="group" aria-label="Thu phóng" className={s.zoom}>
          <button type="button" aria-label="Phóng to" title="Phóng to" onClick={() => setZoom((z) => z + 1)}><IconPlus size={16} stroke={2} aria-hidden="true" /></button>
          <button type="button" aria-label="Thu nhỏ" title="Thu nhỏ" onClick={() => setZoom((z) => z - 1)}><IconMinus size={16} stroke={2} aria-hidden="true" /></button>
          <button type="button" aria-label="Vừa khung" title="Vừa khung" onClick={() => setZoom(0)}><IconFocusCentered size={16} stroke={2} aria-hidden="true" /></button>
        </div>
      </div>
    </Page>
  );
}
